import { useState, useEffect } from "react";
import logo from "../assets/Logo.png";
import ContactUs from "./contact";
import { Link, useNavigate } from "react-router-dom";
import FAQSection from './faq';
import FooterCTA from "./footer";
import { useLanguage } from '../config/language/LanguageContext';

function ScreenContainer({ children }) {
    const { locale, language, setLanguage, languageOptions } = useLanguage();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeLink, setActiveLink] = useState(locale.NAV_BAR.home);
    const navigate = useNavigate();
    const [, setIsSmallScreen] = useState(window.innerWidth < 600);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 600);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto";
    }, [isMobileMenuOpen]);

    useEffect(() => {
        setActiveLink(locale.NAV_BAR.home);
    }, [locale]);

    const menuItems = [
        locale.NAV_BAR.home,
        locale.NAV_BAR.aboutus,
        locale.NAV_BAR.faq,
        locale.NAV_BAR.contact,
    ];

    const sectionMap = {
        [locale.NAV_BAR.home]: locale.NAV_BAR.homeId,
        [locale.NAV_BAR.aboutus]: locale.NAV_BAR.aboutId,
        [locale.NAV_BAR.faq]: locale.NAV_BAR.faqId,
        [locale.NAV_BAR.contact]: locale.NAV_BAR.contactId,
    };

    const scrollToSection = (sectionName) => {
        const sectionId = sectionMap[sectionName] || locale.NAV_BAR.homeId;
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        } else {
            // Redirect to home page with section hash
            navigate(`/#${sectionId}`);
        }
    };

    return (
        <div className="bg-white overflow-x-hidden">
            <header className="bg-white shadow">
                <nav className="max-w-7xl mx-auto flex items-center gap-4 px-6 py-4">
                    <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        to="/">
                        <img src={logo} alt="QR Menu" className="h-10" />
                    </Link>

                    <div className="hidden md:flex items-center space-x-8 justify-end flex-1">
                        {menuItems.map((item) => (
                            <label
                                key={item}
                                onClick={() => {
                                    setActiveLink(item);
                                    scrollToSection(item);
                                }}
                                className={`font-medium transition cursor-pointer ${activeLink === item
                                    ? "text-orange-500"
                                    : "text-gray-600 hover:text-orange-500"
                                    }`}
                            >
                                {item}
                            </label>
                        ))}
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        <label className="sr-only">{locale.LANGUAGE_LABEL || "Select language"}</label>
                        <select
                            aria-label={locale.LANGUAGE_LABEL || "Select language"}
                            value={language}
                            onChange={(event) => setLanguage(event.target.value)}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none"
                        >
                            {languageOptions.map((option) => (
                                <option key={option.code} value={option.code}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="hidden md:flex">
                        <a
                            href="https://form.typeform.com/to/fgpsBEja"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-orange-500 text-white px-4 py-2 rounded-full hover:bg-orange-600 transition text-center"
                        >
                            {locale.NAV_BAR.button}
                        </a>
                    </div>

                    <button
                        className="md:hidden ml-auto"
                        onClick={() => setIsMobileMenuOpen(true)}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-black"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    </button>
                </nav>

                {isMobileMenuOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-95 flex flex-col items-center justify-center text-white text-2xl space-y-8 z-50 px-6">
                        <button
                            className="absolute top-6 right-6 text-white"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-8 w-8"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        {menuItems.map((item) => (
                            <label
                                key={item}
                                onClick={() => {
                                    setActiveLink(item);
                                    setIsMobileMenuOpen(false);
                                    scrollToSection(item);
                                }}
                                className={`transition cursor-pointer ${activeLink === item ? "text-orange-500" : "text-white"
                                    }`}
                            >
                                {item}
                            </label>
                        ))}
                        <button className="bg-orange-500 px-6 py-3 rounded-full">
                            {locale.NAV_BAR.button}
                        </button>

                        <select
                            aria-label={locale.LANGUAGE_LABEL || "Select language"}
                            value={language}
                            onChange={(event) => setLanguage(event.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-800 mt-4"
                        >
                            {languageOptions.map((option) => (
                                <option key={option.code} value={option.code}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </header>

            <main>{children}</main>
            <FAQSection />
            <ContactUs />
            <FooterCTA />
        </div>
    );
}

export default ScreenContainer;

